import React, { useRef, useState } from 'react';
import { UploadCloud, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadDropzoneProps {
  onFileSelect: (files: File[]) => void;
  isUploading?: boolean;
  multiple?: boolean;
  accept?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

export function FileUploadDropzone({
  onFileSelect,
  isUploading = false,
  multiple = false,
  accept = 'image/*,.pdf',
  title = 'Nhấn để chọn hoặc kéo thả tệp vào đây',
  subtitle = 'Hỗ trợ JPG, PNG, PDF. Tối đa 5MB',
  className,
}: FileUploadDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFileSelect(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      onFileSelect(files);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
      className={cn(
        'p-6 sm:p-8 rounded-2xl border-2 border-dashed text-center transition-all cursor-pointer flex flex-col items-center justify-center min-h-[160px]',
        isDragOver ? 'bg-orange-100/60 border-orange-400' : 'bg-orange-50/30 border-orange-200 hover:bg-orange-50/80 hover:border-orange-300',
        isUploading && 'opacity-60 cursor-not-allowed bg-gray-50 border-gray-200',
        className
      )}
    >
      <input
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {isUploading ? (
        <div className="flex flex-col items-center justify-center space-y-2">
          <Loader2 size={32} className="animate-spin text-orange-500 mb-1" />
          <p className="text-sm font-medium text-gray-500">Đang tải tệp lên...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center">
          <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 text-orange-500">
            <UploadCloud size={22} />
          </div>
          <p className="text-sm font-bold text-gray-800 leading-snug">{title}</p>
          <p className="text-xs mt-1 text-gray-500">{subtitle}</p>
        </div>
      )}
    </div>
  );
}

export default FileUploadDropzone;
